import React, { Suspense } from "react";
import { ContainerMixin, ContainerMixinPayload, ContainerPayload, SerializedContainer } from "../mixins/ContainerMixin";
import { EmbedMixin, EmbedMixinPayload, EmbedPayload, SerializedEmbed } from "../mixins/EmbedMixin";
import { $applyNodeReplacement, DecoratorNode, SerializedLexicalNode, Spread } from "lexical";
import { InlineMixin } from "../mixins/InlineMixin";

const InlineComponent = React.lazy(() => import('../mixins/components/InlineComponent'));

export interface InlineEmbedPayload extends ContainerPayload, EmbedPayload {
  key?: string;
}

export type SerializedInlineEmbedNode = Spread<
  SerializedContainer & SerializedEmbed,
  SerializedLexicalNode
>;

class InlineEmbedBase extends DecoratorNode<JSX.Element> implements ContainerMixinPayload, EmbedMixinPayload {
  __embedSrc!: string;
  __width!: number;
  __height!: number;

  constructor(
    embedSrc: string,
    width?: number,
    height?: number,
    key?: string,
  ) {
    super(key);
    this.__width = width || 560;
    this.__height = height || 315;
    this.__embedSrc = embedSrc;
  }

  createDOM(_config: unknown, _editor: unknown): HTMLElement {
    const element = document.createElement('span');
    element.className = 'inline-youtube';
    return element;
  }

  updateDOM(_prevNode: unknown, _dom: HTMLElement, _config: unknown): boolean {
    return false;
  }

  exportJSON(): SerializedInlineEmbedNode {
    const self = this.getLatest();
    return {
      type: InlineEmbedNode.getType(),
      version: 1,
      embedSrc: self.__embedSrc,
      width: self.__width,
      height: self.__height,
    };
  }
}

export class InlineEmbedNode extends EmbedMixin(InlineMixin(ContainerMixin(InlineEmbedBase))) {
  static getType() {
    return 'inline-embed';
  }

  static clone(node: InlineEmbedNode) {
    return new InlineEmbedNode(node.__embedSrc, node.__width, node.__height, node.__key);
  }

  static importJSON(json: SerializedInlineEmbedNode) {
    return new InlineEmbedNode(
      json.embedSrc,
      json.width,
      json.height,
    );
  }

  decorate() {
    return (
      <Suspense fallback={null}>
        <InlineComponent
          nodeKey={this.__key}
        >
          <iframe
            src={this.__embedSrc}
            width={this.__width}
            height={this.__height}
            title="YouTube Video"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen={true}
          />
        </InlineComponent>
      </Suspense>
    );
  }
}

export function $createInlineEmbedNode(
  embedSrc: string,
  width?: number,
  height?: number,
  key?: string,
) {
  return $applyNodeReplacement(new InlineEmbedNode(embedSrc, width, height, key));
}

export function isInlineEmbedNode(node: unknown): node is InlineEmbedNode {
  return node instanceof InlineEmbedNode;
}